export const isValidDate = (date: Date) => date instanceof Date && !Number.isNaN(date?.valueOf());

export const convertBackendTimestamp = (time: number) => time * 1000 - networkTimeDiff;

export const chatTimeFormat = (date: Date | number | string) => {
    if (!(date instanceof Date)) {
        date = new Date(date || null);
    }

    return new Intl.DateTimeFormat('ru-RU', { hour: '2-digit', minute: '2-digit' }).format(date);
};

export const monthsWithDeclensions =
    'января,февраля,марта,апреля,мая,июня,июля,августа,сентября,октября,ноября,декабря'.split(',');

const shortTimestampFormatter = new Intl.DateTimeFormat('ru-ru', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    second: 'numeric',
});

const dateTimeFormatter = new Intl.DateTimeFormat('ru-ru', {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    second: 'numeric',
});

export const shortTimestampFormat = (date: Date | number | string) => {
    if (!(date instanceof Date)) {
        date = new Date(date);
    }

    const p = shortTimestampFormatter.formatToParts(date);

    const day = p[0].value;
    const month = p[2].value;
    const year = p[4].value;

    const hours = p[6].value;
    const minutes = p[8].value;

    return `${day} ${month} ${year} в ${hours}:${minutes}`;
};

const dayInMonthToString = (date: Date) =>
    new Intl.DateTimeFormat('ru', { day: 'numeric', month: 'long' }).format(date);

const isThisYear = (date: Date) => date.getFullYear() === new Date().getFullYear();

const isThisMonth = (date: Date) => isThisYear(date) && date.getMonth() === new Date().getMonth();

const isToday = (date: Date) => isThisMonth(date) && date.getDate() === new Date().getDate();

const isYesterday = (date: Date) => {
    const tempDate = new Date(+date);
    return isToday(new Date(tempDate.setDate(tempDate.getDate() + 1)));
};

const dateToString = (date: Date, absolute = false) => {
    if (absolute || !isThisYear(date)) return `${dayInMonthToString(date)} ${date.getFullYear()}`;

    if (isToday(date)) return `Сегодня, ${dayInMonthToString(date)}`;

    if (isYesterday(date)) return `Вчера, ${dayInMonthToString(date)}`;

    return dayInMonthToString(date);
};

export const chatDateFormat = (date: Date | number | string, absolute = false) => {
    if (!date) return null;

    if (!(date instanceof Date)) {
        date = new Date(date);
    }

    return dateToString(date, absolute);
};

const shortNumericDateFormatter = new Intl.DateTimeFormat('ru-ru', {
    year: '2-digit',
    month: '2-digit',
    day: '2-digit',
});

export const numericShortDateFormat = (date: Date | number | string) => {
    if (!date) return null;

    if (!(date instanceof Date)) {
        date = new Date(date);
    }

    try {
        return shortNumericDateFormatter.format(date);
    } catch {
        return null;
    }
};

export const flipUsEuDate = (dateToFlip: string) => {
    if (!dateToFlip) return null;

    const date = dateToFlip.split('.');

    return `${date[1]}/${date[0]}/${date[2]}`;
};

let networkTimeDiff = 0;

export const setNetworkTimeDiff = (diff: number) => {
    networkTimeDiff = diff;
};

export const getNetworkTime = (date: Date = new Date()) => date.valueOf() - networkTimeDiff;

export const getServerTime = () => Math.round(getNetworkTime() / 1000);

export const formatTime = (seconds: number) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = Math.round(seconds % 60);

    const fh = h ? (h > 9 ? h : `0${h}`) : undefined;
    const fm = m > 9 ? m : `0${m}`;
    const fs = s > 9 ? s : `0${s}`;

    return [fh, fm, fs].filter(Boolean).join(':');
};

export const getTimePresent = () => {
    return performance ? performance.now() + performance.timeOrigin : Date.now();
};

export const formatDateWithMonthDeclension = (date: Date): string => {
    if (!(date instanceof Date)) {
        date = new Date(date);
    }

    const p = dateTimeFormatter.formatToParts(date);

    const day = p[0].value;
    const month = p[2].value;
    const year = p[4].value;

    const hours = p[6].value;
    const minutes = p[8].value;

    return `${day} ${monthsWithDeclensions[+month - 1]} ${year} г., ${hours}:${minutes}`;
};

export const msToMinutes = (timeInMs: number) => timeInMs / 1000 / 60;

export const msToSeconds = (timeInMs: number) => timeInMs / 1000;

export const secondsToMilliseconds = (timeInS: number) => timeInS * 1000;

export const getMinutesDeclension = (minutes: number, textForms: string[]) => {
    const pluckedFirstPass = Math.abs(minutes) % 100;
    const finalPass = pluckedFirstPass % 10;

    if (pluckedFirstPass > 10 && pluckedFirstPass < 20) {
        return textForms[2];
    }
    if (finalPass > 1 && finalPass < 5) {
        return textForms[1];
    }
    if (finalPass == 1) {
        return textForms[0];
    }
    return textForms[2];
};

// for DateSelector and TimeSlot elements
export const adjustSelectedDate = (
    value: string,
    dateType: 'single' | 'range',
    isErasing?: boolean,
) => {
    try {
        const date = value.split(/(..)/g).filter(Boolean);

        if ([3, 6].includes(date.length) || isErasing) return value;

        const adjustValue = (value: string) => {
            const isAutofill = value?.length === 2;
            const numValue = parseInt(value);
            const plusValue = numValue < new Date().getFullYear() - 2000 + 10 ? 2000 : 1900;
            return isAutofill ? String(numValue + plusValue) : value;
        };

        const datePartsQuantity = dateType === 'range' ? 6 : 3;
        if (date.length === datePartsQuantity) {
            date[2] = adjustValue(date[2]);

            if (dateType === 'range') {
                date[5] = adjustValue(date[5]);
            }

            return date.join('');
        }

        return value;
    } catch {
        return value;
    }
};
